import {
  ACCESS_TOKEN_KEY,
  getCodeVerifierFromStorage,
  getRefreshTokenFromStorage,
  setToken,
} from 'apps/t2c-fe/utils';
import {
  AiGenerateTaskMap,
  AvatarCreationTaskMap,
  AvatarVideoTaskMap,
  BrollCaptionGenerateTaskMap,
  ClonedAudioTaskMap,
  Status,
  TextToSpeechTaskMap,
  UpscaleTaskMap,
  User,
  VideoClipperTaskMap,
  WebSocketDataReturnType,
  WebSocketSendMessage,
  WebsocketStatusData,
} from '../../@types';
import { useState, useEffect, useRef } from 'react';
import { sendGTMEvent } from '../../services/analytics';
import { Centrifuge, PublicationContext, Subscription } from 'centrifuge';
import { axiosInstance } from 'apps/t2c-fe/utils';
import { getRefreshAuthToken } from 'apps/t2c-fe/services';

export const useWebSocket = (userHydraId: string) => {
  const [isConnected, setIsConnected] = useState(false);
  const [aiGenerateTaskMap, setAiGenerateTaskMap] = useState<Map<string, AiGenerateTaskMap>>(
    new Map()
  );

  const [brollCaptionTaskMap, setBrollCaptionTaskMap] = useState<
    Map<string, BrollCaptionGenerateTaskMap>
  >(new Map());
  const [avatarCreationTaskMap, setAvatarCreationTaskMap] = useState<
    Map<string, AvatarCreationTaskMap>
  >(new Map());
  const [avatarVideoTaskMap, setAvatarVideoTaskMap] = useState<Map<string, AvatarVideoTaskMap>>(
    new Map()
  );
  const [clonedAudioTaskMap, setClonedAudioTaskMap] = useState<Map<string, ClonedAudioTaskMap>>(
    new Map()
  );
  const [textToSpeechTaskMap, setTextToSpeechTaskMap] = useState<Map<string, TextToSpeechTaskMap>>(
    new Map()
  );
  const [upscaleTaskMap, setUpscaleTaskMap] = useState<Map<string, UpscaleTaskMap>>(new Map());
  const [videoClipperTaskMap, setVideoClipperTaskMap] = useState<Map<string, VideoClipperTaskMap>>(
    new Map()
  );

  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Centrifuge | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  // Added refs for reconnection/backoff control
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalCloseRef = useRef(false);
  const connectingRef = useRef(false);
  const authErrorStreakRef = useRef(0); // consecutive auth/token errors

  useEffect(() => {
    if (!userHydraId) {
      console.warn('HydraId is missing, skipping WebSocket connection');
      return;
    }

    intentionalCloseRef.current = false;

    async function getToken(ctx): Promise<string> {
      const refreshToken = getRefreshTokenFromStorage();
      const codeVerifier = getCodeVerifierFromStorage();

      if (!refreshToken || !codeVerifier) {
        throw new Error('No token found for refreshing');
      }

      const tokenData = await getRefreshAuthToken({ refreshToken, codeVerifier });

      if (!tokenData?.access_token) {
        throw new Error('Failed to refresh access token');
      }

      const { access_token, refresh_token } = tokenData;

      setToken(access_token, refresh_token, codeVerifier);
      return access_token;
    }

    function scheduleReconnect(reason: string) {
      if (intentionalCloseRef.current) return; // don't auto reconnect after unmount
      if (connectingRef.current) return; // already trying
      // throttle if too many auth failures in a row
      if (authErrorStreakRef.current >= 3) {
        const cooldown = 60000; // 60s cooldown after repeated auth failures
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          authErrorStreakRef.current = 0; // reset after cooldown
          scheduleReconnect('auth_cooldown_resume');
        }, cooldown);
        return;
      }
      const attempt = reconnectAttemptsRef.current++;
      const baseDelay = Math.min(30000, 1000 * Math.pow(2, attempt)); // cap at 30s
      const delay = Math.random() * baseDelay;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        if (!socketRef.current) return;
        connectingRef.current = true;
        try {
          socketRef.current.connect();
        } finally {
          // handled by event listeners
        }
      }, delay);
    }

    function clearReconnectState() {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      connectingRef.current = false;
    }

    const socketInstance = new Centrifuge(
      `${process?.env?.NEXT_PUBLIC_WS_URL}/connection/websocket`,
      {
        getToken: function (ctx) {
          return getToken(ctx);
        },
      }
    );
    socketInstance.setToken(localStorage.getItem(ACCESS_TOKEN_KEY));
    socketRef.current = socketInstance;

    const userChannel = `media#${userHydraId}`;

    const attachSubscription = () => {
      if (!socketRef.current) return;
      let sub = socketRef.current.getSubscription(userChannel);
      if (!sub) {
        sub = socketRef.current.newSubscription(userChannel);
        subscriptionRef.current = sub;
        sub
          .on('publication', (ctx: PublicationContext) => {
            const data = ctx.data as WebsocketStatusData;
            if (!data?.id) {
              return;
            }

            if (data?.type === WebSocketDataReturnType.AI_GENERATE) {
              setAiGenerateTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.AD_EVENT) {
              // Trigger GTM/GA event but don't store in task maps
              try {
                const payload: any = (data as any).data || {};
                if (payload?.event) {
                  sendGTMEvent(payload.event, {
                    provider: payload.provider,
                    clickId: payload.clickId,
                    ...payload.params,
                  });
                }
              } catch (e) {
                console.warn('Failed to process adEvent websocket message', e);
              }
            } else if (
              data?.type === WebSocketDataReturnType.CAPTION_BROLL_GENERATE ||
              data?.type === WebSocketDataReturnType.CAPTION_RENDER
            ) {
              setBrollCaptionTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.AVATAR_CREATION) {
              setAvatarCreationTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.AVATAR_VIDEO) {
              setAvatarVideoTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.AUDIO_CLONE) {
              setClonedAudioTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.AUDIO_BUILD) {
              setTextToSpeechTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.UPSCALE) {
              setUpscaleTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            } else if (data?.type === WebSocketDataReturnType.VIDEO_CLIPPER) {
              setVideoClipperTaskMap((prev) => {
                const updatedMap = new Map(prev);
                updatedMap.set(data.id, { ...data });
                return updatedMap;
              });
            }
          })
          .on('error', (error) => {
            console.error('Error while subscribing: ', error);
          })
          .on('subscribed', () => {})
          .subscribe();
      }
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        socketRef.current &&
        socketRef.current.state !== 'connected'
      ) {
        scheduleReconnect('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleOnline = () => {
      if (socketRef.current && socketRef.current.state !== 'connected') {
        scheduleReconnect('online');
      }
    };
    const handleOffline = () => {
      // we could optionally pause here
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    socketRef.current.on('connecting', () => {
      connectingRef.current = true;
    });

    socketRef.current.on('connected', () => {
      clearReconnectState();
      setIsConnected(true);
      setConnectionError(null);
      attachSubscription(); // always ensure subscription after reconnect
    });

    socketRef.current.on('error', async (error) => {
      console.error('Centrifugo connection error: ', error);
      setConnectionError('Socket connection error');
      if (error.error && (error.error.code === 401 || error.error.code === 12)) {
        authErrorStreakRef.current += 1;
        try {
          await axiosInstance.get(`/api`);
          socketRef.current?.setToken(localStorage.getItem(ACCESS_TOKEN_KEY));
          clearReconnectState();
          scheduleReconnect('token_refresh');
        } catch (e) {
          scheduleReconnect('token_refresh_failed');
        }
      } else {
        authErrorStreakRef.current = 0; // reset on non-auth error
        scheduleReconnect('generic_error');
      }
    });

    socketRef.current.on('disconnected', (ctx) => {
      setIsConnected(false);
      connectingRef.current = false;
      if (!intentionalCloseRef.current) {
        scheduleReconnect('disconnected');
      }
    });

    // Initial connect
    scheduleReconnect('initial');

    // Cleanup on unmount
    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try {
        socketRef.current?.disconnect();
      } catch {}
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [userHydraId]);

  const sendMessage = (data: WebSocketSendMessage) => {
    if (socketRef.current && isConnected) {
      // console.log(data);
      // socketRef.current.publish('register', JSON.stringify(data));
    } else {
      setConnectionError('WebSocket is not connected.');
    }
  };

  const handleRemoveFromTaskMap = ({ id, type }: { id: string; type: WebSocketDataReturnType }) => {
    if (type === WebSocketDataReturnType.AI_GENERATE) {
      setAiGenerateTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (
      type === WebSocketDataReturnType.CAPTION_BROLL_GENERATE ||
      type === WebSocketDataReturnType.CAPTION_RENDER
    ) {
      setBrollCaptionTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.AVATAR_CREATION) {
      setAvatarCreationTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.AVATAR_VIDEO) {
      setAvatarVideoTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.AUDIO_CLONE) {
      setClonedAudioTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.AUDIO_BUILD) {
      setTextToSpeechTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.UPSCALE) {
      setUpscaleTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    } else if (type === WebSocketDataReturnType.VIDEO_CLIPPER) {
      setVideoClipperTaskMap((prev) => {
        const updatedMap = new Map(prev);
        updatedMap.delete(id);
        return updatedMap;
      });
    }
  };

  const forceReconnect = () => {
    if (!socketRef.current) return;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    authErrorStreakRef.current = 0;
    if (socketRef.current.state !== 'connected') {
      try {
        connectingRef.current = true;
        socketRef.current.connect();
      } catch (e) {
        console.warn('forceReconnect failed', e);
      }
    }
  };

  const disconnectIntentionally = () => {
    intentionalCloseRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    try {
      socketRef.current?.disconnect();
    } catch {}
  };

  const getConnectionDebug = () => ({
    state: socketRef.current?.state,
    reconnectAttempts: reconnectAttemptsRef.current,
    authErrorStreak: authErrorStreakRef.current,
    hasSubscription: !!subscriptionRef.current,
  });

  return {
    isConnected,
    sendMessage,
    connectionError,
    aiGenerateTaskMap,
    brollCaptionTaskMap,
    handleRemoveFromTaskMap,
    avatarCreationTaskMap,
    avatarVideoTaskMap,
    clonedAudioTaskMap,
    socketRef,
    subscriptionRef,
    textToSpeechTaskMap,
    upscaleTaskMap,
    videoClipperTaskMap,
    forceReconnect,
    disconnectIntentionally,
    getConnectionDebug,
  };
};
